/**
 * useTrial Hook
 *
 * Encapsulates trial banner state logic for the dashboard.
 * Calculates trial day, determines banner visibility and dismiss state.
 */

import { useState, useMemo, useCallback } from "react";
import {
  getTrialDay,
  isTrialExpired,
  TRIAL_DURATION_DAYS,
} from "../services/trialService";

const DISMISS_KEY = "trial-banner-dismissed";

interface TrialState {
  /** Current trial day (1-based). 0 if no createdAt provided. */
  trialDay: number;
  /** Total trial days */
  totalDays: number;
  /** Whether to show the trial banner */
  showBanner: boolean;
  /** Whether the banner can be dismissed (Days 1-5 only) */
  dismissible: boolean;
  /** Whether to show the blocking UpgradePrompt modal (expired) */
  showBlockingModal: boolean;
  /** Dismiss handler for the banner */
  handleDismiss: () => void;
}

/**
 * Hook to manage trial banner visibility and state.
 *
 * @param createdAt - User's account creation date (trial start)
 * @returns Trial state for rendering banner and modal
 */
export function useTrial(createdAt: Date | undefined): TrialState {
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // localStorage unavailable — dismiss for this session only
    }
  }, []);

  return useMemo(() => {
    if (!createdAt) {
      return {
        trialDay: 0,
        totalDays: TRIAL_DURATION_DAYS,
        showBanner: false,
        dismissible: false,
        showBlockingModal: false,
        handleDismiss: () => {},
      };
    }

    const trialDay = getTrialDay(createdAt);
    const expired = isTrialExpired(createdAt);
    const dismissible = trialDay <= 5;

    return {
      trialDay,
      totalDays: TRIAL_DURATION_DAYS,
      showBanner: !expired && !isDismissed,
      dismissible,
      showBlockingModal: expired,
      handleDismiss,
    };
  }, [createdAt, isDismissed, handleDismiss]);
}
